/**
 * Reusable async primitives for call media (timeouts, abort, one-shot deferreds).
 * Keep GUM / WebRTC work abortable at the caller without leaking unhandled rejections.
 *
 * Timeouts use TimeoutError so Sentry still records them. AbortError is only for
 * real cancellation (hangup / unmount / AbortSignal).
 */

export function createAbortError(message = 'Aborted') {
  return Object.assign(new Error(message), { name: 'AbortError' });
}

export function createTimeoutError(message = 'Timed out') {
  return Object.assign(new Error(message), { name: 'TimeoutError' });
}

export function isTimeoutError(err) {
  return Boolean(err && typeof err === 'object' && err.name === 'TimeoutError');
}

/**
 * Race `promise` against a timer (and optional AbortSignal). A late reject from
 * `promise` is swallowed so a timeout/abort cannot leave an unhandled rejection.
 *
 * @template T
 * @param {Promise<T> | T} promise
 * @param {number} timeoutMs
 * @param {string} [message]
 * @param {AbortSignal} [signal]
 * @returns {Promise<T>}
 */
export function withTimeout(promise, timeoutMs, message = 'Timed out', signal) {
  const pending = Promise.resolve(promise);
  pending.catch(() => { });

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (settle, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(id);
      signal?.removeEventListener('abort', onAbort);
      settle(value);
    };

    const id = setTimeout(() => {
      finish(reject, createTimeoutError(message));
    }, timeoutMs);

    const onAbort = () => {
      finish(reject, createAbortError());
    };

    if (signal) {
      if (signal.aborted) {
        finish(reject, createAbortError());
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }

    pending.then(
      (value) => finish(resolve, value),
      (error) => finish(reject, error)
    );
  });
}

/**
 * AbortSignal that fires after `timeoutMs`. Call `dispose()` when the operation
 * finishes so a late abort cannot cancel a follow-up step.
 *
 * `timedOut` is true only when the timer fired — not when `abort()` was called
 * for hangup/unmount — so callers can map that path to TimeoutError.
 *
 * @param {number} timeoutMs
 * @returns {{
 *   signal: AbortSignal,
 *   abort: () => void,
 *   dispose: () => void,
 *   readonly timedOut: boolean,
 * }}
 */
export function createTimeoutController(timeoutMs) {
  const controller = new AbortController();
  let timedOut = false;
  let timeoutId = setTimeout(() => {
    timeoutId = null;
    timedOut = true;
    try {
      controller.abort();
    } catch (_) { }
  }, timeoutMs);

  const clearTimer = () => {
    if (timeoutId == null) return;
    clearTimeout(timeoutId);
    timeoutId = null;
  };

  return {
    signal: controller.signal,
    get timedOut() {
      return timedOut;
    },
    abort() {
      clearTimer();
      try {
        controller.abort();
      } catch (_) { }
    },
    dispose() {
      clearTimer();
    },
  };
}

/**
 * Deferred that can only settle once. Used to unblock a caller on abort while
 * background cleanup (late getUserMedia) keeps the work queue occupied.
 *
 * @template T
 * @returns {{
 *   promise: Promise<T>,
 *   resolve: (value: T) => void,
 *   reject: (error: unknown) => void,
 *   readonly settled: boolean,
 * }}
 */
export function createDeferred() {
  let settled = false;
  /** @type {(value: T) => void} */
  let resolveFn;
  /** @type {(error: unknown) => void} */
  let rejectFn;
  const promise = new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });

  return {
    promise,
    resolve(value) {
      if (settled) return;
      settled = true;
      resolveFn(value);
    },
    reject(error) {
      if (settled) return;
      settled = true;
      rejectFn(error);
    },
    get settled() {
      return settled;
    },
  };
}
